// App.tsx
// import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store/store";
import Login from "./pages/Login";
import TenantTnc from "./pages/TenantTnc";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import TermsAndConditions from "./pages/TermsAndConditions";
import Layout from "./pages/Layout";
import TenantDashboard from "./modules/tenant/TenantDashboard";
import VerifyPage from "./modules/otpVerification/verifyPage";
import GuestDashboard from "./modules/tenant/GuestDashboard";
import LandingPageBuilderContent from "./modules/superAdmin/LandingPageBuilderContent";
import CampaignPageBuilder from "./modules/superAdmin/CampaignPageBuilder";
import Dashboard from "./modules/superAdmin/Dashboard";
import TenantUsers from "./modules/superAdmin/TenantUsers";
import AssetUploadComponent from "./modules/superAdmin/AssetUploadComponent";
import AuditLogs from "./modules/superAdmin/AuditLogs";
import TicketTracker from "./modules/tickets/TicketTracker";
import Profile from "./pages/Profile";
import TenantTncManagement from "./pages/TenantTncManagement";
import ProtectedRoute from "./pages/ProtectedRoute";
import CustomerUserManagement from "./modules/tenant/CustomerUserManagement";
import { useSelector } from "react-redux";
import type { RootState } from "./store/store";
import SuperAdminDashboard from "../src/modules/superAdmin/SuperAdminDashboard";
import GuestSharePage from "../src/modules/tenant/GuestSharePage";
import { ThemeProvider } from "./modules/superAdmin/ThemeProvider";
import type { ReactNode } from "react";
import CustomerManagerDashboard from "./modules/superAdmin/CustomerManagerDashbaord";
const RequireSignedIn = ({ children }: { children: ReactNode }) => {
  return localStorage.getItem("user") ? (
    <>{children}</>
  ) : (
    <Navigate to="/login" replace />
  );
};

function App() {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/tenant-tnc" element={<TenantTnc />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/terms"
            element={<TermsAndConditions onAcceptance={() => undefined} />}
          />
          <Route path="/verify/:token" element={<VerifyPage />} />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route
            path="/unauthorized"
            element={
              <div
                style={{
                  minHeight: "100vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div style={{ fontSize: "48px" }}>🚫</div>
                <h1 style={{ fontSize: "24px", fontWeight: 700 }}>
                  Access Denied
                </h1>
                <p style={{ color: "#666" }}>
                  You don't have permission to view this page.
                </p>
              </div>
            }
          />

          {/* Guest routes */}
          <Route path="/guest/dashboard" element={<GuestDashboard />} />
          <Route path="/guest/access/:token" element={<VerifyPage />} />
          <Route path="/guest/shares/:token" element={<GuestSharePage />} />
          <Route
            path="/tickets/*"
            element={
              <ProtectedRoute>
                <TicketTracker />
              </ProtectedRoute>
            }
          />

          {/* Super Admin routes */}
          <Route path="/super-admin/*" element={<Layout />}>
            <Route
              path="super-admin-dashboard"
              element={
                <ProtectedRoute>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="tenant-users"
              element={
                <ProtectedRoute>
                  <TenantUsers />
                </ProtectedRoute>
              }
            />

            <Route
              path="tenants/:tenantId/tnc"
              element={
                <ProtectedRoute>
                  <TenantTncManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="roles"
              element={
                <ProtectedRoute>
                  <div className="p-6">Roles — coming soon</div>
                </ProtectedRoute>
              }
            />

            <Route
              path="landing-page-builder"
              element={
                <ProtectedRoute>
                  <LandingPageBuilderContent />
                </ProtectedRoute>
              }
            />

            <Route
              path="system-oversight"
              element={
                <ProtectedRoute>
                  <div className="p-6">System Oversight — coming soon</div>
                </ProtectedRoute>
              }
            />

            <Route
              path="campaign-builder"
              element={
                <ProtectedRoute>
                  <CampaignPageBuilder
                    tenant={{ name: "SIMB" }}
                    onSave={() => undefined}
                    onClose={() => undefined}
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="asset-manager"
              element={
                <ProtectedRoute>
                  <AssetUploadComponent />
                </ProtectedRoute>
              }
            />

            <Route
              path="audit-logs"
              element={
                <ProtectedRoute>
                  <AuditLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path="settings"
              element={
                <ProtectedRoute>
                  <div>Super Admin Settings</div>
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Tenant routes */}
          <Route
            path="/tenant/*"
            element={
              <ThemeProvider>
                <Layout />
              </ThemeProvider>
            }
          >
            <Route
              index
              element={<Navigate to="/tenant/dashboard" replace />}
            />

            <Route
              path="dashboard"
              element={
                <ProtectedRoute>
                  <TenantDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />

            <Route
              path="users"
              element={
                <ProtectedRoute requiredPermission="USER_VIEW">
                  <TenantUsersRouteWrapper />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route
            path="/customer-manager/*"
            element={
              <ThemeProvider>
                <Layout />
              </ThemeProvider>
            }
          >
            <Route
              index
              element={<Navigate to="/customer-manager/dashboard" replace />}
            />
            <Route
              path="dashboard"
              element={
                <ProtectedRoute>
                  <CustomerManagerDashboard />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>

      </BrowserRouter>
    </Provider>
  );
}

const TenantUsersRouteWrapper: React.FC = () => {
  const tenantId = useSelector(
    (state: RootState) => state.auth.user?.tenantId || "",
  );
  return <CustomerUserManagement tenantId={tenantId} />;
};

export default App;
